import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  services: [],
  selected_service: null,
  loading: false,
  isSuccess: false,
  recordsTotal: 0,
  errors: [],
};
export const fetchAllServices = createAsyncThunk('services/fetchAllServices', async (_, { rejectWithValue, dispatch }) => {
  try {
    const response = await api.get('/services/allServices');
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) dispatch(logout());
    if (!error.response) throw error;
    return rejectWithValue(error.response.data);
  }
});
export const fetchServices = createAsyncThunk('services/fetchServices', async (params, { rejectWithValue, dispatch }) => {
  try {
    const response = await api.get('/services', { params });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) dispatch(logout());
    if (!error.response) throw error;
    return rejectWithValue(error.response.data);
  }
});

export const getServiceById = createAsyncThunk('services/getServiceById', async (serviceId, { rejectWithValue, dispatch }) => {
  try {
    const response = await api.get(`/services/${serviceId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) dispatch(logout());
    if (!error.response) throw error;
    return rejectWithValue(error.response.data);
  }
});

export const addService = createAsyncThunk('services/addService', async (serviceData, { rejectWithValue, dispatch }) => {
  try {
    // ✅ Extraction et conversion en string de l'ID
    const businessAreaId = typeof serviceData.businessArea === 'object'
      ? serviceData.businessArea?._id?.toString()
      : serviceData.businessArea?.toString();

    const params = {
      name: serviceData.serviceName,
      business_area: businessAreaId,
      active: serviceData.active,
      order: serviceData.order,
    };
    if (serviceData.sourceLang) params.sourceLang = serviceData.sourceLang;

    const response = await api.post('/services', params);
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) dispatch(logout());
    if (!error.response) throw error;
    return rejectWithValue(error.response.data);
  }
});

export const deleteService = createAsyncThunk('services/deleteService', async (serviceId, { rejectWithValue, dispatch }) => {
  try {
    const response = await api.delete(`/services/${serviceId}`);
    dispatch(fetchServices());
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) dispatch(logout());
    if (!error.response) throw error;
    return rejectWithValue(error.response.data);
  }
});

export const updateService = createAsyncThunk('services/updateService', async (updatedService, { rejectWithValue, dispatch }) => {
  try {
    const { id, name, business_area, active, order, sourceLang } = updatedService;

    const businessAreaId = typeof business_area === 'object'
      ? business_area?._id?.toString()
      : business_area?.toString();

    const params = {
      name,
      business_area: businessAreaId,
      active,
      order,
    };
    if (sourceLang) params.sourceLang = sourceLang;

    const response = await api.put(`/services/${id}`, params);
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) dispatch(logout());
    if (!error.response) throw error;
    return rejectWithValue(error.response.data);
  }
});

const servicesSlice = createSlice({
  name: 'services',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchServices.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.services = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
        state.loading = false;
      })
      .addCase(fetchServices.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(deleteService.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteService.fulfilled, (state) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(deleteService.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(addService.pending, (state) => {
        state.loading = true;
      })
      .addCase(addService.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        const newService = { ...payload, DT_RowId: payload._id };
        state.services.push(newService);
        state.loading = false;
      })
      .addCase(addService.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(updateService.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateService.fulfilled, (state, { payload }) => {
        const updatedId = payload.DT_RowId || payload._id;
        state.services = state.services.map((service) => {
          const serviceId = service.DT_RowId || service._id;
          return serviceId === updatedId ? { ...payload, DT_RowId: updatedId, _id: updatedId } : service;
        });
        state.loading = false;
      })
      .addCase(updateService.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(getServiceById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServiceById.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.selected_service = payload;
        state.loading = false;
      })
      .addCase(getServiceById.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      }).addCase(fetchAllServices.pending, (state) => {
  state.loading = true;
})
.addCase(fetchAllServices.fulfilled, (state, { payload }) => {
  state.isSuccess = true;
  state.services = payload || [];
  state.loading = false;
})
.addCase(fetchAllServices.rejected, (state, { payload }) => {
  if (payload?.statusCode === 400) state.errors = payload?.message;
  state.isSuccess = false;
  state.loading = false;
})
      
  }
});

export default servicesSlice.reducer;